// M3 waterflood & EOR projects (M3_SPEC §11): per reservoir, gated by method date, upgrade, API gravity and CO₂
// sources; ≥ 2 player wells (one becomes the injector); unitization (≥ 50% of the reservoir's plots) where rivals
// lease; after lagDays the recoverable volume rises by eorRfAdd × OOIP over rampDays; opex on incremental barrels.
// Samotlor-style depleted starts (reservoir.startRemainingFrac) are applied once when the field opens.
import { wellsOnField } from '../indexes.js';
import { fieldDef, yf } from '../defs.js';
import { inflate } from '../../data/cpi.js';
import { EOR_METHODS, eorRfAdd } from '../../data/m3/rules.js';
import { m3, m3On } from './opsutil.js';
import { reservoirsAt } from '../geo.js';
import { hasUpgrade } from '../research.js';
import { fieldOpen } from '../survey.js';
import { spend, fmt$, notify } from '../util.js';

const ok = (msg, extra = {}) => ({ ok: true, msg, ...extra });
const no = (msg) => ({ ok: false, msg });
export const eorKey = (fid, resId) => `${fid}:${resId}`;

export function resDef(fid, resId) {
  for (const p of fieldDef(fid)?.prospects ?? []) for (const r of p.reservoirs ?? []) if (r.id === resId) return r;
  return null;
}

export function eorCheck(state, fieldId, resId, method) {
  const m = EOR_METHODS[method];
  if (!m) return { reason: 'Unknown recovery method.' };
  const f = state.fields[fieldId];
  const r = resDef(fieldId, resId);
  const rs = f?.reservoirs?.[resId];
  if (!r || !rs) return { reason: 'No such reservoir.' };
  const y = yf(state);
  if (y < m.from) return { reason: `${m.label} is not practised before ${Math.floor(m.from)}.` };
  if (m.upgrade && !hasUpgrade(state, m.upgrade)) return { reason: `Research ${m.upgrade.replace(/_/g, ' ')} first.` };
  if (m.heavyOnly && !(r.heavy || (r.api ?? 35) < 22)) return { reason: `Steam is for heavy oil (API < 22); this crude is ${r.api ?? 35}°.` };
  if (m.minApi && (r.api ?? 35) < m.minApi) return { reason: `CO₂ needs light crude (API ≥ ${m.minApi}).` };
  if (m.needs === 'co2') {
    const rid = fieldDef(fieldId)?.regionId;
    if (!(m.co2Sources ?? []).some((s) => y >= s.from && s.regionIds.includes(rid))) return { reason: 'No CO₂ source reaches this field.' };
  }
  if (state.m3?.eor?.[eorKey(fieldId, resId)]) return { reason: 'A project is already running on this reservoir.' };
  const wells = wellsOnField(state, fieldId).filter((w) => w.reservoirId === resId && !w.ownerOperatorId && ['PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(w.status));
  if (wells.length < 2) return { reason: 'Needs at least two of your wells on the reservoir (one becomes the injector).' };
  const fdef = fieldDef(fieldId);
  const plots = f.plots.map((p, i) => i).filter((i) => reservoirsAt(fdef, f.plots[i]).some((x) => x.id === resId));
  const rivals = plots.some((i) => f.plots[i].taken);
  const owned = plots.filter((i) => f.plots[i].owned).length;
  if (rivals && owned < plots.length * 0.5) return { reason: `Unitize first: you hold ${owned} of ${plots.length} plots over the reservoir (need half), or the neighbours free-ride.` };
  const cost = inflate(m.plantCost + m.capexPerWell * wells.length, m.baseYear, Math.min(y, 2025));
  const ooip = rs.initial / (r.rf ?? 0.25);
  return { reason: null, m, r, rs, wells, cost, ooip, rfAdd: eorRfAdd(r, method) };
}

export function startEorImpl(state, fieldId, resId, method) {
  if (!m3On(state)) return no('Organized EOR projects begin after 1946.');
  const c = eorCheck(state, fieldId, resId, method);
  if (c.reason) return no(c.reason);
  if (!spend(state, c.cost, 'capex')) return no(`The ${c.m.label.toLowerCase()} plant and well work cost ${fmt$(c.cost)}.`);
  const inj = [...c.wells].sort((a, b) => (a.prod?.rate ?? 0) - (b.prod?.rate ?? 0))[0];
  inj.injector = true;
  if (inj.prod) { inj.prod.shutIn = true; inj.prod.rate = 0; }
  inj.status = inj.status === 'DEPLETED' ? 'SHUT_IN' : inj.status;
  m3(state).eor[eorKey(fieldId, resId)] = { fieldId, resId, method, startDay: state.day, lagDays: c.m.lagDays, rampDays: c.m.rampDays, rfAdd: c.rfAdd, ooip: Math.round(c.ooip), applied: 0, injectorId: inj.id, cost: Math.round(c.cost) };
  state.company.stats.eorProjects = (state.company.stats.eorProjects ?? 0) + 1;
  return ok(`${c.m.label} started on ${c.r.name ?? resId}: ${fmt$(c.cost)}; ${inj.name} converted to an injector. Response in ~${Math.round(c.m.lagDays / 30)} months.`);
}

let DEPLETED_START = null;
export function tickEor(state) {
  const P = state.m3?.eor;
  if (P) for (const p of Object.values(P)) {
    const rs = state.fields[p.fieldId]?.reservoirs?.[p.resId];
    if (!rs) continue;
    const prog = Math.max(0, Math.min(1, (state.day - p.startDay - p.lagDays) / Math.max(1, p.rampDays)));
    const target = p.rfAdd * p.ooip * prog;
    const delta = target - p.applied;
    if (delta > 0) { rs.initial += delta; rs.remaining += delta; p.applied = target; }
    if (prog > 0) {
      const m = EOR_METHODS[p.method];
      let q = 0;
      for (const w of wellsOnField(state, p.fieldId)) if (w.reservoirId === p.resId) q += w.prod?.rate ?? 0;
      const inc = q * (p.applied / Math.max(1, rs.initial));
      const cost = inc * (inflate(m.opexPerBbl, m.baseYear, Math.min(yf(state), 2025)) + (m.fuelBblPerBbl ?? 0) * (state.market?.spot ?? 0));
      if (cost > 0) spend(state, cost, 'opex', false);
      if (prog >= 1 && !p.full) { p.full = true; notify(state, 'news', { fieldId: p.fieldId, kind: 'production', text: `${m.label} on ${resDef(p.fieldId, p.resId)?.name ?? p.resId} at full response.`, level: 'info' }); }
    }
  }
  // depleted starts (Samotlor JV 1991: others produced it for decades)
  DEPLETED_START ??= Object.values(Object.fromEntries(Object.keys(state.fields).map((k) => [k, fieldDef(k)])))
    .filter((d) => d?.prospects?.some((p) => p.reservoirs?.some((r) => r.startRemainingFrac != null))).map((d) => d.id);
  for (const fid of DEPLETED_START) {
    const f = state.fields[fid];
    if (!f || f.depletedStart || !fieldOpen(state, fid)) continue;
    f.depletedStart = state.day;
    for (const p of fieldDef(fid).prospects) for (const r of p.reservoirs ?? []) {
      if (r.startRemainingFrac == null || !f.reservoirs[r.id]) continue;
      if (wellsOnField(state, fid).some((w) => w.reservoirId === r.id)) continue;
      f.reservoirs[r.id].remaining = Math.round(f.reservoirs[r.id].initial * r.startRemainingFrac);
      f.reservoirs[r.id].startWaterCut = r.startWaterCut ?? 0;
    }
  }
}

export function eorView(state, fieldId) {
  const fdef = fieldDef(fieldId);
  if (!fdef) return null;
  const out = [];
  for (const p of fdef.prospects ?? []) for (const r of p.reservoirs ?? []) {
    const proj = state.m3?.eor?.[eorKey(fieldId, r.id)] ?? null;
    const methods = Object.keys(EOR_METHODS).map((k) => { const c = eorCheck(state, fieldId, r.id, k); return { method: k, label: EOR_METHODS[k].label, ok: !c.reason, reason: c.reason, cost: c.cost ?? null, rfAdd: c.rfAdd ?? eorRfAdd(r, k) }; });
    const prog = proj ? Math.max(0, Math.min(1, (state.day - proj.startDay - proj.lagDays) / Math.max(1, proj.rampDays))) : 0;
    out.push({ resId: r.id, name: r.name, api: r.api, heavy: !!r.heavy, project: proj ? { ...proj, progress: prog, addedBbl: Math.round(proj.applied) } : null, methods });
  }
  return { fieldId, reservoirs: out };
}

